import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Home,
  LogOut,
  Menu,
  X,
  FileText,
  Settings,
  Copy,
  Check,
  Users,
  MousePointerClick,
  DollarSign,
  Clock,
  CheckCircle,
  TrendingUp,
  Shield,
  Handshake,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const commissionStatusConfig = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-700" },
  validated: { label: "Validée", color: "bg-blue-100 text-blue-700" },
  to_be_invoiced: { label: "À facturer", color: "bg-indigo-100 text-indigo-700" },
  paid: { label: "Payée", color: "bg-green-100 text-green-700" },
  cancelled: { label: "Annulée", color: "bg-red-100 text-red-700" } };

const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [iban, setIban] = useState("");
  const [copied, setCopied] = useState(false);
  const [showCommissions, setShowCommissions] = useState(true);
  const [conversionData, setConversionData] = useState([]);
  const [commissionPage, setCommissionPage] = useState(0);
  const COMMISSIONS_PER_PAGE = 10;

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/affiliates/me`, { withCredentials: true });
      setAffiliate(data);
      const [commResp, stmtResp, convResp] = await Promise.allSettled([
        axios.get(`${API_URL}/api/affiliates/commissions?limit=100`, { withCredentials: true }),
        axios.get(`${API_URL}/api/affiliates/monthly-statements`, { withCredentials: true }),
        axios.get(`${API_URL}/api/affiliates/conversion-stats?days=30`, { withCredentials: true }),
      ]);
      if (commResp.status === "fulfilled") {
        setCommissions(commResp.value.data);
        setCommissionPage(0);
      }
      if (stmtResp.status === "fulfilled") {
        setStatements(stmtResp.value.data);
      }
      if (convResp.status === "fulfilled") {
        setConversionData(convResp.value.data.map(d => ({
          ...d,
          label: new Date(d.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) })));
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setAffiliate(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      await axios.post(`${API_URL}/api/affiliates/register`, { iban: iban || null }, { withCredentials: true });
      toast.success("Demande envoyée ! En attente de validation.");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setRegistering(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const affiliateLink = affiliate ? `${window.location.origin}/?ref=${affiliate.affiliate_code}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(affiliateLink);
    setCopied(true);
    toast.success("Lien copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatAmount = (a) => {
    if (a === null || a === undefined) return "-";
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(a);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400"><X className="w-6 h-6" /></button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-2">
            {user?.role === "admin" && (
              <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
                <Shield className="w-5 h-5" /> Administration
              </Link>
            )}
            <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
              <LayoutDashboard className="w-5 h-5" /> Tableau de bord
            </Link>
            <Link to="/logements" className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg">
              <Home className="w-5 h-5" /> Mes logements
            </Link>
            <Link to="/partenaire" className="flex items-center gap-3 px-4 py-3 text-[#19294C] bg-slate-100 rounded-lg font-medium">
              <Handshake className="w-5 h-5" /> Programme partenaire
            </Link>
          </nav>
          <div className="p-4 border-t border-slate-100">
            <div className="px-4 py-3 mb-2">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg w-full text-left">
              <LogOut className="w-5 h-5" /> Déconnexion
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600"><Menu className="w-6 h-6" /></button>
              <Handshake className="w-6 h-6 text-[#19294C]" />
              <h1 className="text-xl font-semibold text-[#19294C] font-['Outfit']">Programme Partenaire</h1>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8" data-testid="affiliate-dashboard">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00B8E4]" />
            </div>
          ) : !affiliate ? (
            /* Registration Form */
            <div className="max-w-lg mx-auto mt-12">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center" data-testid="affiliate-register-form">
                <Handshake className="w-16 h-16 text-[#19294C] mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-[#19294C] font-['Outfit'] mb-2">Devenez partenaire</h2>
                <p className="text-slate-500 mb-6">Gagnez <strong>10% de commission</strong> sur chaque souscription générée via votre lien affilié.</p>
                <div className="text-left mb-6">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">IBAN (optionnel)</label>
                  <Input
                    placeholder="FR76 3000 6000 0112 3456 7890 189"
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    data-testid="affiliate-iban-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Vous pourrez l'ajouter plus tard.</p>
                </div>
                <Button
                  className="w-full bg-[#00B8E4] hover:bg-[#15803d] text-white"
                  onClick={handleRegister}
                  disabled={registering}
                  data-testid="affiliate-register-btn"
                >
                  {registering ? "Envoi en cours..." : "Soumettre ma candidature"}
                </Button>
              </div>
            </div>
          ) : affiliate.status === "pending" ? (
            /* Pending State */
            <div className="max-w-lg mx-auto mt-12">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center" data-testid="affiliate-pending">
                <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-[#19294C] font-['Outfit'] mb-2">En attente de validation</h2>
                <p className="text-slate-500 mb-4">Votre demande est en cours d'examen. Vous recevrez un email dès l'activation.</p>
                <p className="text-sm text-slate-400">Code affilié : <span className="font-mono font-medium text-slate-600">{affiliate.affiliate_code}</span></p>
              </div>
            </div>
          ) : affiliate.status === "suspended" ? (
            <div className="max-w-lg mx-auto mt-12">
              <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 text-center">
                <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-red-600 font-['Outfit'] mb-2">Compte suspendu</h2>
                <p className="text-slate-500">Contactez support@hostprotect.io pour plus d'informations.</p>
              </div>
            </div>
          ) : (
            /* Active Dashboard */
            <>
              {/* Affiliate Link */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="affiliate-link-section">
                <h3 className="text-sm font-medium text-slate-500 mb-3">Votre lien affilié</h3>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Input
                      readOnly
                      value={affiliateLink}
                      className="pr-12 font-mono text-sm bg-slate-50"
                      data-testid="affiliate-link-input"
                    />
                  </div>
                  <Button onClick={copyLink} variant="outline" data-testid="affiliate-copy-btn">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-2">{copied ? "Copié" : "Copier"}</span>
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2">Commission : {affiliate.commission_rate}% par souscription</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="affiliate-stats">
                {[
                  { label: "Clics", value: affiliate.stats?.total_clicks || 0, icon: MousePointerClick, bg: "bg-blue-100", ic: "text-blue-600" },
                  { label: "Inscriptions", value: affiliate.stats?.total_referrals || 0, icon: Users, bg: "bg-indigo-100", ic: "text-indigo-600" },
                  { label: "Total gagné", value: formatAmount(affiliate.stats?.total_earned || 0), icon: TrendingUp, bg: "bg-green-100", ic: "text-green-600" },
                  { label: "En attente", value: formatAmount(affiliate.stats?.pending || 0), icon: Clock, bg: "bg-amber-100", ic: "text-amber-600" },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                      <div className={`w-10 h-10 ${s.bg} rounded-lg flex items-center justify-center mb-3`}>
                        <Icon className={`w-5 h-5 ${s.ic}`} />
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                      <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Conversion Chart */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6 overflow-hidden" data-testid="conversion-chart">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h3 className="text-base font-semibold text-[#19294C] font-['Outfit']">Conversions — 30 derniers jours</h3>
                </div>
                <div className="p-6">
                  {conversionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={conversionData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorReg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#19294C" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#19294C" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorComm" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00B8E4" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#00B8E4" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                        <Tooltip
                          contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px" }}
                          formatter={(value, name) => {
                            const labels = { clicks: "Clics", registrations: "Inscriptions", commissions: "Paiements" };
                            return [value, labels[name] || name];
                          }}
                          labelFormatter={(label) => label}
                        />
                        <Legend formatter={(value) => {
                          const labels = { clicks: "Clics", registrations: "Inscriptions", commissions: "Paiements" };
                          return labels[value] || value;
                        }} />
                        <Area type="monotone" dataKey="clicks" stroke="#6366f1" strokeWidth={2} fill="url(#colorClicks)" />
                        <Area type="monotone" dataKey="registrations" stroke="#19294C" strokeWidth={2} fill="url(#colorReg)" />
                        <Area type="monotone" dataKey="commissions" stroke="#00B8E4" strokeWidth={2} fill="url(#colorComm)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-8">Aucune donnée disponible</p>
                  )}
                </div>
              </div>

              {/* Monthly Statements */}
              {statements.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6 overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-100">
                    <h3 className="text-base font-semibold text-[#19294C] font-['Outfit']">Relevés mensuels</h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {statements.map((s) => (
                      <div key={s.month} className="px-6 py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{s.month}</p>
                          <p className="text-xs text-slate-500">{s.commission_count} commission(s)</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-slate-900">{formatAmount(s.total_commissions)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${commissionStatusConfig[s.status]?.color || "bg-gray-100 text-gray-700"}`}>
                            {commissionStatusConfig[s.status]?.label || s.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Commissions Table */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <button
                  className="w-full px-6 py-4 flex items-center justify-between border-b border-slate-100 hover:bg-slate-50"
                  onClick={() => setShowCommissions(!showCommissions)}
                >
                  <h3 className="text-base font-semibold text-[#19294C] font-['Outfit']">Historique des commissions</h3>
                  {showCommissions ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </button>
                {showCommissions && (
                  commissions.length === 0 ? (
                    <div className="p-8 text-center">
                      <DollarSign className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Aucune commission pour le moment</p>
                      <p className="text-slate-400 text-xs mt-1">Partagez votre lien pour commencer à gagner</p>
                    </div>
                  ) : (
                    <>
                    <div className="overflow-x-auto" data-testid="commissions-table">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-100 text-xs text-slate-500">
                            <th className="px-6 py-3 text-left font-medium">Date</th>
                            <th className="px-6 py-3 text-left font-medium">Type</th>
                            <th className="px-6 py-3 text-left font-medium">Montant</th>
                            <th className="px-6 py-3 text-left font-medium">Commission</th>
                            <th className="px-6 py-3 text-left font-medium">Statut</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {commissions
                            .slice(commissionPage * COMMISSIONS_PER_PAGE, (commissionPage + 1) * COMMISSIONS_PER_PAGE)
                            .map((c) => {
                            const cfg = commissionStatusConfig[c.status] || commissionStatusConfig.pending;
                            return (
                              <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                                <td className="px-6 py-3 text-sm text-slate-600">{formatDate(c.created_at)}</td>
                                <td className="px-6 py-3">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.type === "initial" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                                    {c.type === "initial" ? "Initial" : "Renouvellement"}
                                  </span>
                                </td>
                                <td className="px-6 py-3 text-sm text-slate-600">{formatAmount(c.amount)}</td>
                                <td className="px-6 py-3 text-sm font-medium text-green-600">{formatAmount(c.commission_amount)}</td>
                                <td className="px-6 py-3">
                                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {commissions.length > COMMISSIONS_PER_PAGE && (
                      <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100" data-testid="commissions-pagination">
                        <p className="text-xs text-slate-500">
                          {commissionPage * COMMISSIONS_PER_PAGE + 1}
                          -
                          {Math.min((commissionPage + 1) * COMMISSIONS_PER_PAGE, commissions.length)}
                          {" "}sur{" "}
                          {commissions.length}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setCommissionPage((p) => Math.max(0, p - 1))}
                            disabled={commissionPage === 0}
                            className="p-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Page précédente"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="text-xs text-slate-600 min-w-[60px] text-center">
                            Page {commissionPage + 1} / {Math.ceil(commissions.length / COMMISSIONS_PER_PAGE)}
                          </span>
                          <button
                            type="button"
                            onClick={() => setCommissionPage((p) => Math.min(Math.ceil(commissions.length / COMMISSIONS_PER_PAGE) - 1, p + 1))}
                            disabled={(commissionPage + 1) * COMMISSIONS_PER_PAGE >= commissions.length}
                            className="p-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Page suivante"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                  )
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
